/**
 * Bundle Selector Component
 * Handles bundle quantity selection (Single, Duo, Trio) with size options.
 * Ported from Horizon theme, adapted for Paletia (Dawn-based).
 *
 * Integration points:
 * - variant-selects: Paletia's variant picker (Dawn-based, uses <select> elements)
 * - PUB_SUB_EVENTS: Paletia's pub/sub system (variantChange, cartUpdate)
 * - cart-drawer: Paletia's cart drawer component (open, renderContents)
 * - TeeInBlue: Personalization system (intercepts form submit)
 */
const TRIO_DEFAULT_QUANTITY = 3;
const TRIO_MAX_QUANTITY = 20;

class BundleSelectorComponent extends HTMLElement {
  constructor() {
    super();
    this.bundleType = 'single';
    this.isProcessing = false;
    this.trioQuantity = TRIO_DEFAULT_QUANTITY;
    this._lastPriceCents = 0;
  }

  connectedCallback() {
    // positionBeforeSubmit() re-inserts this element to move it before the submit button,
    // which triggers a second connectedCallback. Guard so listeners are only attached once.
    if (this._initialized) return;
    this._initialized = true;
    this.cacheElements();
    this.attachEventListeners();
    this.syncInitialSize();
    this.positionBeforeSubmit();
  }

  // ============ INITIALIZATION ============

  cacheElements() {
    this.productId = this.dataset.productId;
    this.sectionId = this.dataset.sectionId;
    this.radioInputs = this.querySelectorAll('input[name="bundle_type"]');
    this.quantityInput = this.querySelector('.bundle-quantity-input');
    this.variantsSelect = this.querySelector('.hidden-variants');

    // Trio stepper elements
    const trioCard = this.querySelector('input[value="trio"] + .bundle-card');
    this.stepperEl = trioCard?.querySelector('.bundle-trio-stepper');
    this.stepperDecBtn = trioCard?.querySelector('.bundle-stepper-btn[data-stepper-action="decrement"]');
    this.stepperIncBtn = trioCard?.querySelector('.bundle-stepper-btn[data-stepper-action="increment"]');
    this.stepperValueEl = trioCard?.querySelector('.bundle-stepper-value');
    this.trioSizeSelectors = trioCard?.querySelector('.bundle-size-selectors');
    this.trioRowTemplate = trioCard?.querySelector('template[data-trio-row-template]');
    this._lastPriceCents = parseInt(this.dataset.basePrice) || 0;

    // Find the product form (Paletia wraps it in <product-form>)
    const productFormEl = document.querySelector(`#product-form-${this.sectionId}`) ||
                          document.querySelector('product-form form') ||
                          document.querySelector('form[action="/cart/add"]');
    this.form = productFormEl?.tagName === 'FORM' ? productFormEl : productFormEl?.querySelector('form');
  }

  /**
   * Reposition the bundle right before the submit button once.
   * TeeInBlue injects its form dynamically, so we wait and move once.
   */
  positionBeforeSubmit() {
    if (this._positioned) return;

    const doPosition = () => {
      if (this._positioned) return;
      const submitBtn = this.form?.querySelector('button[type="submit"][name="add"]');
      if (submitBtn && submitBtn.parentNode && this.parentNode) {
        submitBtn.parentNode.insertBefore(this, submitBtn);
        this._positioned = true;
      }
    };

    setTimeout(doPosition, 500);
    setTimeout(doPosition, 1500);
  }

  attachEventListeners() {
    // Bundle type radio changes
    this.radioInputs.forEach(radio => {
      radio.addEventListener('change', (e) => this.handleBundleChange(e.target.value));
    });

    // Size dropdown changes within this component
    this.addEventListener('change', (e) => {
      if (e.target.classList.contains('size-dropdown')) {
        this.handleSizeChange(e.target);
      }
    });

    // Sync with Paletia's variant picker via pub/sub
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data?.variant) {
          this._lastPriceCents = event.data.variant.price;
          this.updateBundlePrices(event.data.variant.price);
        }
      });
    }

    // Also listen to variant-selects directly for size sync
    this.listenToVariantPicker();

    // Trio quantity stepper
    this.setupTrioStepper();

    // Intercept form submission for bundles
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }
  }

  // ============ SIZE SYNCHRONIZATION ============

  listenToVariantPicker() {
    const variantPicker = document.querySelector('variant-selects');
    if (!variantPicker) return;

    variantPicker.addEventListener('change', (e) => {
      // Paletia uses <select> elements for variant options, or radio/swatch inputs
      if (e.target.tagName === 'SELECT' || e.target.type === 'radio') {
        if (this.isSizeInput(e.target)) {
          this.syncBundleSizes(e.target.value);
        }
      }
    });
  }

  isSizeInput(input) {
    // For <select> elements: check the label associated with it
    if (input.tagName === 'SELECT') {
      const fieldset = input.closest('fieldset');
      const legend = fieldset?.querySelector('legend, .form__label');
      return legend?.textContent.match(/Maat|Size|Grootte|Größe/i);
    }
    // For radio inputs: check fieldset legend
    const fieldset = input.closest('fieldset');
    const legend = fieldset?.querySelector('legend, .form__label');
    return legend?.textContent.match(/Maat|Size|Grootte|Größe/i);
  }

  syncInitialSize() {
    setTimeout(() => {
      const currentSize = this.getSelectedSize();
      if (currentSize) this.syncBundleSizes(currentSize);
    }, 100);
  }

  getSelectedSize() {
    const variantPicker = document.querySelector('variant-selects');
    if (!variantPicker) return null;

    // Paletia: check <select> elements first
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      if (this.isSizeInput(select)) return select.value;
    }

    // Fallback: check radio inputs
    const radios = variantPicker.querySelectorAll('input[type="radio"]:checked');
    for (const radio of radios) {
      if (this.isSizeInput(radio)) return radio.value;
    }

    return null;
  }

  syncBundleSizes(size) {
    // Sync #1 size selector with main variant picker selection
    const firstSelectors = [
      this.querySelector('select[name="duo_size_1"]'),
      this.querySelector('select[name="trio_size_1"]')
    ];

    firstSelectors.forEach(selector => {
      if (selector && selector.querySelector(`option[value="${CSS.escape(size)}"]`)) {
        selector.value = size;
      }
    });
  }

  // ============ BUNDLE MANAGEMENT ============

  handleBundleChange(type) {
    this.bundleType = type;
    if (type !== 'trio') this.resetTrioQuantity();
    this.updateVisibleSizeSelectors();
    this.updateQuantityInput();
    this.dispatchBundleEvent();
  }

  updateVisibleSizeSelectors() {
    // Hide all size selectors
    this.querySelectorAll('.bundle-size-selectors').forEach(el => {
      el.style.display = 'none';
    });

    // Show selected bundle's size selectors
    const selectedRadio = this.querySelector(`input[value="${this.bundleType}"]:checked`);
    if (selectedRadio) {
      const card = selectedRadio.nextElementSibling;
      const sizeSelectors = card?.querySelector('.bundle-size-selectors');
      if (sizeSelectors) sizeSelectors.style.display = 'flex';
    }
  }

  updateQuantityInput() {
    if (this.quantityInput) {
      this.quantityInput.value = 1;
    }
    // Also update main form quantity
    const mainQuantityInput = document.querySelector('input[name="quantity"]');
    if (mainQuantityInput && mainQuantityInput !== this.quantityInput) {
      mainQuantityInput.value = 1;
    }
  }

  handleSizeChange(dropdown) {
    // If first size selector changed, sync with main variant picker
    if (dropdown.name === 'duo_size_1' || dropdown.name === 'trio_size_1') {
      this.selectSizeInMainPicker(dropdown.value);
    }
  }

  selectSizeInMainPicker(size) {
    const variantPicker = document.querySelector('variant-selects');
    if (!variantPicker) return;

    // Paletia: try <select> elements first
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      if (this.isSizeInput(select)) {
        select.value = size;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    // Fallback: try radio inputs
    const radios = variantPicker.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if (this.isSizeInput(radio) && radio.value === size) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }

  dispatchBundleEvent() {
    const itemCount = this.bundleType === 'trio' ? this.trioQuantity :
                      this.bundleType === 'duo' ? 2 : 1;

    this.dispatchEvent(new CustomEvent('bundle-quantity-change', {
      detail: { bundleType: this.bundleType, itemCount },
      bubbles: true
    }));
  }

  // ============ TRIO STEPPER ============

  setupTrioStepper() {
    if (!this.stepperEl) return;
    this.stepperDecBtn?.addEventListener('click', () => this.handleStepperChange(-1));
    this.stepperIncBtn?.addEventListener('click', () => this.handleStepperChange(+1));
    this.updateStepperUI();
  }

  handleStepperChange(delta) {
    const next = this.trioQuantity + delta;
    if (next < TRIO_DEFAULT_QUANTITY || next > TRIO_MAX_QUANTITY) return;
    this.trioQuantity = next;
    this.updateTrioRows();
    this.updateStepperUI();
    this.updateBundlePrices(this._lastPriceCents);
    if (this.bundleType === 'trio') this.dispatchBundleEvent();
  }

  updateTrioRows() {
    if (!this.trioSizeSelectors || !this.trioRowTemplate) return;
    const rows = this.trioSizeSelectors.querySelectorAll('.size-selector-row');
    const current = rows.length;
    if (this.trioQuantity > current) {
      for (let i = current + 1; i <= this.trioQuantity; i++) {
        const node = this.trioRowTemplate.content.cloneNode(true);
        const row = node.querySelector('.size-selector-row');
        row.querySelector('.size-label').textContent = `#${i}`;
        const sel = row.querySelector('.size-dropdown');
        sel.name = `trio_size_${i}`;
        sel.selectedIndex = 0;
        // Insert before the <template> so template stays last
        this.trioSizeSelectors.insertBefore(row, this.trioRowTemplate);
      }
    } else if (this.trioQuantity < current) {
      for (let i = current; i > this.trioQuantity; i--) rows[i - 1].remove();
    }
  }

  updateStepperUI() {
    if (!this.stepperValueEl) return;
    this.stepperValueEl.textContent = this.trioQuantity;
    if (this.stepperDecBtn) this.stepperDecBtn.disabled = this.trioQuantity <= TRIO_DEFAULT_QUANTITY;
    if (this.stepperIncBtn) this.stepperIncBtn.disabled = this.trioQuantity >= TRIO_MAX_QUANTITY;
  }

  resetTrioQuantity() {
    if (this.trioQuantity === TRIO_DEFAULT_QUANTITY) return;
    this.trioQuantity = TRIO_DEFAULT_QUANTITY;
    this.updateTrioRows();
    this.updateStepperUI();
    this.updateBundlePrices(this._lastPriceCents);
  }

  // ============ FORM SUBMISSION ============

  async handleFormSubmit(e) {
    // For single, let the normal product-form handle it (including TeeInBlue)
    if (this.bundleType === 'single') return;

    // Prevent default for bundle selections
    e.preventDefault();
    e.stopPropagation();

    if (this.isProcessing) return;

    await this.addBundleToCart();
  }

  async addBundleToCart() {
    this.isProcessing = true;

    const button = this.form?.querySelector('button[type="submit"]');
    this.setButtonState(button, true);

    try {
      const sizes = this.getSelectedSizes();
      this.validateSizes(sizes);

      let variantIds;

      // If no sizes (product has no size option), use current variant multiple times
      if (sizes.length === 0) {
        const currentVariantId = this.getCurrentVariantId();
        if (!currentVariantId) throw new Error('Could not find current variant');

        const bundleCount = this.bundleType === 'duo' ? 2 : this.trioQuantity;
        variantIds = Array(bundleCount).fill(currentVariantId);
      } else {
        variantIds = this.getVariantIds(sizes);
        if (variantIds.length === 0) throw new Error('Could not find variants for selected sizes');
      }

      const bundleItemCount = variantIds.length;

      // Step 1: Set quantity for TeeInBlue addon calculation
      this.setQuantity(bundleItemCount);

      // Step 2: Try TeeInBlue flow first (clicks add-to-cart to trigger personalization)
      const teeInBlueSubmitted = await this.submitThroughTeeInBlue();

      if (!teeInBlueSubmitted) {
        // Fallback: direct cart add if no TeeInBlue
        await this.addItemDirectly(variantIds[0]);
      }

      // Step 3: Add remaining bundle items (first was added by TeeInBlue or direct add)
      const remainingVariants = variantIds.slice(1);
      if (remainingVariants.length > 0) {
        await this.addAllItemsToCart(remainingVariants);
      }

      // Step 4: Open cart drawer and refresh
      await this.refreshCartAndOpen();

      // Reset
      this.setQuantity(1);
      this.resetSelection();
    } catch (error) {
      this.showError(error.message);
      this.setQuantity(1);
    } finally {
      this.isProcessing = false;
      this.setButtonState(button, false);
    }
  }

  setQuantity(qty) {
    const quantityInputs = document.querySelectorAll('input[name="quantity"]');
    quantityInputs.forEach(input => { input.value = qty; });
  }

  async submitThroughTeeInBlue() {
    return new Promise((resolve) => {
      let cartUpdateDetected = false;

      const cartUpdateHandler = () => { cartUpdateDetected = true; };

      // Listen for Paletia's cart update
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, cartUpdateHandler);
        // Also listen for DOM event fallback
        document.addEventListener('cart:update', cartUpdateHandler, { once: true });
      }

      // Find and click the actual submit button to trigger TeeInBlue's flow
      const productFormComponent = document.querySelector('product-form');
      const actualButton = productFormComponent?.querySelector('button[type="submit"]');

      if (actualButton) {
        actualButton.click();

        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max

        const checkComplete = setInterval(() => {
          attempts++;
          if (cartUpdateDetected || attempts >= maxAttempts) {
            clearInterval(checkComplete);
            document.removeEventListener('cart:update', cartUpdateHandler);
            resolve(cartUpdateDetected);
          }
        }, 100);
      } else {
        resolve(false);
      }
    });
  }

  /**
   * Collects all form properties (TeeInBlue customization fields, etc.)
   * from the product form to include with each cart item.
   */
  getFormProperties() {
    if (!this.form) return {};

    const properties = {};
    const propertyInputs = this.form.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');

    propertyInputs.forEach(input => {
      const match = input.name.match(/properties\[([^\]]+)\]/);
      if (match && input.value) {
        properties[match[1]] = input.value;
      }
    });

    return properties;
  }

  async addItemDirectly(variantId) {
    const properties = this.getFormProperties();
    const body = { id: parseInt(variantId), quantity: 1 };
    if (Object.keys(properties).length > 0) body.properties = properties;

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }
    return response.json();
  }

  async addAllItemsToCart(variantIds) {
    const properties = this.getFormProperties();
    const hasProperties = Object.keys(properties).length > 0;

    const items = variantIds.map(id => {
      const item = { id: parseInt(id), quantity: 1 };
      if (hasProperties) item.properties = properties;
      return item;
    });

    // Get sections to render for cart drawer update
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sectionIds = cart?.getSectionsToRender?.()?.map(s => s.id) || [];

    const requestBody = { items };
    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add items to cart');
    }

    return response.json();
  }

  async refreshCartAndOpen() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cart) return;

    // Small delay to ensure all items are registered in the cart
    await new Promise(r => setTimeout(r, 500));

    // Fetch fresh cart with section rendering for drawer + icon bubble
    const sectionIds = cart.getSectionsToRender?.()?.map(s => s.id) || [];
    const sectionsParam = sectionIds.length > 0 ? `?sections=${sectionIds.join(',')}` : '';

    try {
      const response = await fetch(`/cart.js${sectionsParam}`);
      const cartData = await response.json();

      // Publish cart update via Paletia's pub/sub
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-selector',
          cartData: cartData
        });
      }

      // Update cart drawer + cart icon bubble with fresh section HTML
      if (typeof cart.renderContents === 'function' && cartData.sections) {
        cart.renderContents(cartData);
      } else if (typeof cart.open === 'function') {
        cart.open();
      }

      // Also update cart icon bubble directly as fallback
      const bubbleSection = cartData.sections?.['cart-icon-bubble'];
      if (bubbleSection) {
        const bubbleEl = document.getElementById('cart-icon-bubble');
        if (bubbleEl) {
          bubbleEl.innerHTML = new DOMParser()
            .parseFromString(bubbleSection, 'text/html')
            .querySelector('.shopify-section')?.innerHTML || bubbleEl.innerHTML;
        }
      }
    } catch (err) {
      // Fallback: just open the drawer
      if (typeof cart.open === 'function') cart.open();
    }
  }

  // ============ VARIANT LOOKUP ============

  getSelectedSizes() {
    const selector = this.bundleType === 'duo'
      ? '.bundle-option input[value="duo"]:checked + .bundle-card'
      : '.bundle-option input[value="trio"]:checked + .bundle-card';

    const card = this.querySelector(selector);
    if (!card) return [];

    const dropdowns = card.querySelectorAll('.size-dropdown');
    return Array.from(dropdowns)
      .map(d => d.value)
      .filter(v => v && v.trim() !== '');
  }

  validateSizes(sizes) {
    const required = this.bundleType === 'duo' ? 2 : this.trioQuantity;
    // No validation needed for products without size options
    if (sizes.length === 0) return;
    if (sizes.length < required) {
      throw new Error(`Please select all ${required} sizes (${sizes.length}/${required} selected)`);
    }
  }

  getVariantIds(sizes) {
    return sizes
      .map(size => this.findVariantBySize(size))
      .filter(id => id !== null);
  }

  findVariantBySize(size) {
    if (!this.variantsSelect) return null;

    const options = this.variantsSelect.querySelectorAll('option');

    // Strategy 1: Match with current non-size options (e.g. Color / Size)
    const nonSizeOptions = this.getNonSizeOptions();
    const fullTitle = [...nonSizeOptions, size].join(' / ');

    for (const option of options) {
      const title = option.dataset.title || option.textContent.trim();
      if (title === fullTitle) return option.value;
    }

    // Strategy 2: Match size at end of variant title
    for (const option of options) {
      const title = option.dataset.title || option.textContent.trim();
      const parts = title.split(/\s*[\/\-]\s*/);
      if (parts[parts.length - 1].trim() === size.trim()) return option.value;
    }

    return null;
  }

  getNonSizeOptions() {
    const variantPicker = document.querySelector('variant-selects');
    if (!variantPicker) return [];

    const options = [];

    // Paletia: check <select> elements
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      if (!this.isSizeInput(select)) {
        options.push(select.value);
      }
    }

    // Fallback: check radio inputs
    if (options.length === 0) {
      const radios = variantPicker.querySelectorAll('input[type="radio"]:checked');
      for (const radio of radios) {
        if (!this.isSizeInput(radio)) {
          options.push(radio.value);
        }
      }
    }

    return options;
  }

  getCurrentVariantId() {
    // Try form hidden input first
    if (this.form) {
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput?.value) return idInput.value;
    }

    // Fallback: hidden variants select
    if (this.variantsSelect) {
      const selected = this.variantsSelect.querySelector('option[selected], option');
      if (selected) return selected.value;
    }

    return null;
  }

  // ============ DYNAMIC PRICE UPDATES ============

  updateBundlePrices(newPriceCents) {
    const duoDiscountRaw = parseFloat(this.dataset.duoDiscount) || 0;
    const trioDiscountRaw = parseFloat(this.dataset.trioDiscount) || 0;

    // Single
    const singlePriceEl = this.querySelector('input[value="single"] + .bundle-card .bundle-price .price');
    if (singlePriceEl) singlePriceEl.textContent = this.formatMoney(newPriceCents);

    // Duo
    const duoTotal = newPriceCents * 2;
    let duoPrice = duoTotal;
    if (duoDiscountRaw > 0) {
      duoPrice = duoDiscountRaw <= 1
        ? Math.round(duoTotal * duoDiscountRaw)
        : duoTotal - Math.round(duoTotal * duoDiscountRaw / 100);
    }

    const duoPriceEl = this.querySelector('input[value="duo"] + .bundle-card .bundle-price .price');
    const duoOrigEl = this.querySelector('input[value="duo"] + .bundle-card .bundle-price .price-original');
    if (duoPriceEl) duoPriceEl.textContent = this.formatMoney(duoPrice);
    if (duoOrigEl && duoDiscountRaw > 0) duoOrigEl.textContent = this.formatMoney(duoTotal);

    // Trio (scales with stepper quantity)
    const trioTotal = newPriceCents * this.trioQuantity;
    let trioPrice = trioTotal;
    if (trioDiscountRaw > 0) {
      trioPrice = trioDiscountRaw <= 1
        ? Math.round(trioTotal * trioDiscountRaw)
        : trioTotal - Math.round(trioTotal * trioDiscountRaw / 100);
    }

    const trioPriceEl = this.querySelector('input[value="trio"] + .bundle-card .bundle-price .price');
    const trioOrigEl = this.querySelector('input[value="trio"] + .bundle-card .bundle-price .price-original');
    if (trioPriceEl) trioPriceEl.textContent = this.formatMoney(trioPrice);
    if (trioOrigEl && trioDiscountRaw > 0) trioOrigEl.textContent = this.formatMoney(trioTotal);
  }

  formatMoney(cents) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountComma = amount.replace('.', ',');
    const amountNoDecimals = Math.round(cents / 100).toString();

    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace('.', "'"))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  // ============ UI HELPERS ============

  setButtonState(button, isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle('loading', isLoading);
  }

  showError(message) {
    const errorContainer = document.querySelector('.product-form__error-message-wrapper');
    if (errorContainer) {
      const errorMsg = errorContainer.querySelector('.product-form__error-message');
      if (errorMsg) errorMsg.textContent = message;
      errorContainer.removeAttribute('hidden');
      setTimeout(() => errorContainer.setAttribute('hidden', ''), 5000);
    } else {
      console.error('Bundle error:', message);
    }
  }

  resetSelection() {
    const singleRadio = this.querySelector('input[value="single"]');
    if (singleRadio) {
      singleRadio.checked = true;
      this.handleBundleChange('single');
    }
  }
}

// Register component
if (!customElements.get('bundle-selector-component')) {
  customElements.define('bundle-selector-component', BundleSelectorComponent);
}
